import { IconUser16 } from '@warp-ds/icons/react';
import { Select } from '@warp-ds/react/components/select';
import { useEffect, useState } from 'react';
import { Dropdown } from 'shared/search/common/Dropdown';
import { CustomNumberInput } from '../common/CustomNumberInput';

export interface PaxCombinationProps {
    numberOfAdults: number;
    agesOfChildren: number[];
    label: string;
    updatePax: (numberOfAdults: number, agesOfChildren: number[]) => void;
    className?: string;
    hideLabelOnSmallScreen?: boolean;
}

const MAX_PAX = 4;
const MAX_CHILDREN = 2;

const childAgeOptions = Array.from({ length: 18 }, (_, i) => ({
    value: `${i}`,
    label: i === 0 ? 'Under 1 år' : `${i}`,
}));

export const PaxCombination = ({
    numberOfAdults,
    agesOfChildren,
    label,
    updatePax,
    className = '',
    hideLabelOnSmallScreen,
}: PaxCombinationProps) => {
    const [adults, setAdults] = useState(numberOfAdults);
    const [agesChildren, setAgesChildren] = useState(agesOfChildren);

    const updateAdults = (newAdults: number) => {
        setAdults(newAdults);
        updatePax(newAdults, agesChildren);
    };

    const setChildrenCount = (newCount: number) => {
        const adjustedChildren =
            newCount > agesChildren.length
                ? [...agesChildren, ...new Array(newCount - agesChildren.length).fill(0)]
                : agesChildren.slice(0, newCount);
        setAgesChildren(adjustedChildren);
        updatePax(adults, adjustedChildren);
    };

    const setChildAge = (childIndex: number, age: number) => {
        const updatedChildrensAges = [...agesChildren];
        updatedChildrensAges[childIndex] = age;
        setAgesChildren(updatedChildrensAges);
        updatePax(adults, updatedChildrensAges);
    };

    useEffect(() => {
        setAdults(numberOfAdults);
    }, [numberOfAdults]);

    useEffect(() => {
        setAgesChildren(agesOfChildren);
    }, [agesOfChildren]);

    const isMaxAdults = adults + agesChildren.length >= MAX_PAX;

    const isMaxChildren = agesChildren.length >= MAX_CHILDREN || adults + agesChildren.length >= MAX_PAX;

    return (
        <Dropdown
            trigger={{
                text: `${adults === 1 ? '1 voksen' : `${adults} voksne`}${agesChildren.length > 0 ? `, ${agesChildren.length} barn` : ''}`,
                label: label,
                hideLabelOnSmallScreens: hideLabelOnSmallScreen,
            }}
            prefix={<IconUser16 />}
            className={`sm:flex ${className}`}>
            <fieldset key={`paxCombination-${adults}-${agesChildren.length}`} className="flex flex-col gap-12">
                <CustomNumberInput
                    id="number-of-adults-input"
                    label="Voksne"
                    value={adults}
                    setNumber={(val) => updateAdults(val)}
                    min={1}
                    max={4}
                    disableSubtract={adults <= 1}
                    disableAdd={isMaxAdults}
                />
                <CustomNumberInput
                    id="age-of-children-input"
                    label="Barn"
                    helpText="0-12 år"
                    value={agesChildren.length}
                    setNumber={(val) => setChildrenCount(val)}
                    min={0}
                    max={2}
                    disableSubtract={agesChildren.length <= 0}
                    disableAdd={isMaxChildren}
                />
                {agesChildren.map((childAge, childIndex) => (
                    <Select
                        key={`age-of-children-select-${childIndex}-${childAge}`}
                        value={`${childAge}`}
                        onChange={(val) => setChildAge(childIndex, Number.parseInt(val.target.value))}
                        label={`Alder på barn ${childIndex + 1}`}>
                        {childAgeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                ))}
            </fieldset>
        </Dropdown>
    );
};
